/**
 * Netze length/height steppers (Netconsult app): mobile-friendly taps + 500ms calc delay.
 */
(function () {
  const STEPPER_SELECTOR = '.net-length-stepper-btn';
  const CALC_DELAY_MS = 500;
  const ABSOLUTE_MIN = 0;
  const LENGTH_INPUT_PREFIX = 'net-length-input-';
  const HEIGHT_INPUT_PREFIX = 'net-height-input-';
  const HEIGHT_MIN_M = 1.0;
  const TOOLTIP_HIDE_MS = 2800;
  const TOOLTIP_GLOBAL_ID = 'net-length-input-tooltip-global';
  const calculatorTimers = new WeakMap();
  const tooltipTimers = new WeakMap();
  let activeTooltipInput = null;
  let tooltipRepositionHandler = null;
  const nativeInputValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

  function getLengthLimits() {
    return window.NetzeLengthLimits || null;
  }

  function isLengthInput(input) {
    return Boolean(input && input.id && input.id.startsWith(LENGTH_INPUT_PREFIX));
  }

  function isHeightInput(input) {
    return Boolean(input && input.id && input.id.startsWith(HEIGHT_INPUT_PREFIX));
  }

  function isDimensionInput(input) {
    return isLengthInput(input) || isHeightInput(input);
  }

  function isForcedDecimalFormat(draft, next) {
    if (draft === next) return false;

    const draftNormalized = draft.replace(',', '.').trim();
    const nextNormalized = next.replace(',', '.').trim();
    if (!draftNormalized || !nextNormalized) return false;
    if (/[.,]/.test(draft)) return false;

    const draftNumber = Number.parseFloat(draftNormalized);
    if (!Number.isFinite(draftNumber)) return false;

    return nextNormalized === `${draftNumber}.0`;
  }

  function markDimensionInputUserChange(event) {
    const input = event.currentTarget;
    if (input._netzeValueGuardState) {
      input._netzeValueGuardState.fromUser = true;
    }
  }

  function beginDimensionInputEdit(input) {
    if (input.dataset.netzeValueGuardActive === 'true') return;
    if (!nativeInputValue || !nativeInputValue.get || !nativeInputValue.set) return;

    input.dataset.netzeValueGuardActive = 'true';
    input.dataset.netzeDraftValue = input.value;

    const state = { fromUser: false };
    input._netzeValueGuardState = state;

    Object.defineProperty(input, 'value', {
      configurable: true,
      get() {
        return nativeInputValue.get.call(this);
      },
      set(nextValue) {
        const next = String(nextValue ?? '');

        if (state.fromUser) {
          state.fromUser = false;
          input.dataset.netzeDraftValue = next;
          nativeInputValue.set.call(this, next);
          return;
        }

        const draft = String(input.dataset.netzeDraftValue ?? '');
        if (isForcedDecimalFormat(draft, next)) {
          return;
        }

        input.dataset.netzeDraftValue = next;
        nativeInputValue.set.call(this, next);
      },
    });

    input.addEventListener('beforeinput', markDimensionInputUserChange);
  }

  function endDimensionInputEdit(input) {
    if (input.dataset.netzeValueGuardActive !== 'true') return;
    if (!nativeInputValue || !nativeInputValue.get || !nativeInputValue.set) {
      delete input._netzeValueGuardState;
      delete input.dataset.netzeValueGuardActive;
      delete input.dataset.netzeDraftValue;
      return;
    }

    const current = nativeInputValue.get.call(input);
    input.removeEventListener('beforeinput', markDimensionInputUserChange);
    delete input._netzeValueGuardState;
    delete input.dataset.netzeValueGuardActive;
    delete input.dataset.netzeDraftValue;

    Object.defineProperty(input, 'value', nativeInputValue);
    nativeInputValue.set.call(input, current);
  }

  function isDimensionInputEditing(input) {
    return isDimensionInput(input) && input.dataset.netzeValueGuardActive === 'true';
  }

  function blockDimensionInputEventsWhileEditing(event) {
    const input = event.target;
    if (!isDimensionInputEditing(input)) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  function markLengthInputUserChange(event) {
    markDimensionInputUserChange(event);
  }

  function beginLengthInputEdit(input) {
    beginDimensionInputEdit(input);
  }

  function endLengthInputEdit(input) {
    endDimensionInputEdit(input);
  }

  function isLengthInputEditing(input) {
    return isDimensionInputEditing(input);
  }

  function blockLengthInputEventsWhileEditing(event) {
    blockDimensionInputEventsWhileEditing(event);
  }

  function getProductInfoRoot(input) {
    return input ? input.closest('product-info') : null;
  }

  function getLengthInputRow(input) {
    return input.closest('.net-length-input-row-main') || input.closest('.net-length-input-row');
  }

  function ensureLengthAlert(input) {
    const row = getLengthInputRow(input);
    if (!row) return null;

    let alert = row.querySelector('[data-net-length-alert]');
    if (!alert) {
      alert = document.createElement('div');
      alert.className = 'net-length-alert';
      alert.setAttribute('data-net-length-alert', '');
      alert.setAttribute('role', 'alert');
      alert.hidden = true;
      row.appendChild(alert);
    }

    return alert;
  }

  function hideLengthAlert(input) {
    const alert = input ? ensureLengthAlert(input) : document.querySelector('[data-net-length-alert]:not([hidden])');
    if (!alert) return;
    alert.hidden = true;
    alert.replaceChildren();
    alert.removeAttribute('data-alert-type');

    const row = alert.closest('.net-length-input-row-main, .net-length-input-row');
    if (row) row.classList.remove('net-length-input-row--alert-visible');
  }

  function setLengthAlertContent(alert, content) {
    alert.replaceChildren();

    const messageText = typeof content === 'string' ? content : content.message;
    if (!messageText) return;

    const message = document.createElement('p');
    message.className = 'net-length-alert__message';
    message.textContent = messageText;
    alert.appendChild(message);

    if (typeof content === 'object' && content.linkHref && content.linkText) {
      const link = document.createElement('a');
      link.className = 'net-length-alert__link';
      link.href = content.linkHref;
      link.textContent = content.linkText;
      alert.appendChild(link);
    }
  }

  function showLengthAlert(input, content, alertType) {
    if (!content || !isLengthInput(input)) return;

    const alert = ensureLengthAlert(input);
    if (!alert) return;

    const resolvedType =
      alertType ||
      (typeof content === 'object' && content.alertType ? content.alertType : 'unavailable');

    setLengthAlertContent(alert, content);
    alert.dataset.alertType = resolvedType;
    alert.removeAttribute('hidden');

    const row = getLengthInputRow(input);
    if (row) row.classList.add('net-length-input-row--alert-visible');

    hideLengthTooltip(input);
  }

  function showLengthFeedback(input, attemptedValue) {
    const content = getLengthAttemptMessage(input, attemptedValue);
    if (!content) {
      hideLengthAlert(input);
      return;
    }

    showLengthAlert(input, content, content.alertType);
  }

  function parseNum(raw) {
    const parsed = Number.parseFloat(String(raw ?? '').trim().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function formatForInput(value, input) {
    const text = value.toFixed(1);
    return (input.value || '').includes(',') ? text.replace('.', ',') : text;
  }

  function getMin(input) {
    if (isLengthInput(input)) {
      const limits = getLengthLimits();
      return limits ? limits.LENGTH_MIN_M : 1.0;
    }

    if (isHeightInput(input)) {
      return HEIGHT_MIN_M;
    }

    const attrMin = parseNum(input.min);
    if (attrMin !== null) return Math.max(ABSOLUTE_MIN, attrMin);
    return ABSOLUTE_MIN;
  }

  function getMax(input) {
    if (isLengthInput(input)) {
      const root = getProductInfoRoot(input);
      const limits = getLengthLimits();
      if (root && limits) return limits.getEffectiveMaxLength(root);
    }

    const attrMax = parseNum(input.max);
    return attrMax !== null ? attrMax : Number.POSITIVE_INFINITY;
  }

  function ensureLengthTooltip() {
    let tooltip = document.getElementById(TOOLTIP_GLOBAL_ID);
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.id = TOOLTIP_GLOBAL_ID;
      tooltip.className = 'net-length-input-tooltip';
      tooltip.setAttribute('role', 'status');
      tooltip.setAttribute('aria-live', 'polite');
      document.body.appendChild(tooltip);
    }

    return tooltip;
  }

  function getTooltipAnchor(input) {
    return input.closest('.net-length-number-stepper') || input;
  }

  function positionLengthTooltip(input, tooltip) {
    const anchor = getTooltipAnchor(input);
    const rect = anchor.getBoundingClientRect();
    tooltip.style.left = `${rect.left + rect.width / 2}px`;
    tooltip.style.top = `${rect.top}px`;
  }

  function bindTooltipReposition(input, tooltip) {
    unbindTooltipReposition();
    tooltipRepositionHandler = () => positionLengthTooltip(input, tooltip);
    window.addEventListener('scroll', tooltipRepositionHandler, true);
    window.addEventListener('resize', tooltipRepositionHandler, { passive: true });
    window.visualViewport?.addEventListener('resize', tooltipRepositionHandler, { passive: true });
  }

  function unbindTooltipReposition() {
    if (!tooltipRepositionHandler) return;
    window.removeEventListener('scroll', tooltipRepositionHandler, true);
    window.removeEventListener('resize', tooltipRepositionHandler);
    window.visualViewport?.removeEventListener('resize', tooltipRepositionHandler);
    tooltipRepositionHandler = null;
  }

  function hideLengthTooltip(input) {
    const tooltip = document.getElementById(TOOLTIP_GLOBAL_ID);
    if (!tooltip) return;

    if (input && activeTooltipInput !== input) return;

    tooltip.classList.remove('is-visible');
    tooltip.textContent = '';
    activeTooltipInput = null;
    unbindTooltipReposition();
  }

  function showLengthTooltip(input, message) {
    if (!message || !isLengthInput(input)) return;
    showLengthAlert(input, message);
  }

  function getLengthAttemptMessage(input, attemptedValue) {
    const limits = getLengthLimits();
    const root = getProductInfoRoot(input);
    if (!limits || !root || typeof limits.getLengthFeedbackContent !== 'function') return null;

    return limits.getLengthFeedbackContent(root, attemptedValue);
  }

  function clampInputValue(input, options = {}) {
    if (isDimensionInputEditing(input)) return input;

    const min = getMin(input);
    const max = getMax(input);
    let current = parseNum(input.value);
    if (current === null) return input;

    const attempted = current;
    if (isLengthInput(input)) {
      const root = getProductInfoRoot(input);
      const limits = getLengthLimits();
      if (root && limits) {
        current = limits.clampLengthValue(root, current);
      } else {
        current = Math.max(min, Math.min(max, current));
      }

      if (options.showTooltip !== false && attempted !== current) {
        showLengthFeedback(input, attempted);
      }
    } else {
      current = Math.max(min, Math.min(max, current));
    }

    input.value = formatForInput(current, input);
    return input;
  }

  function updateStepperButtons(container) {
    container.querySelectorAll(STEPPER_SELECTOR).forEach((button) => {
      const input = getInputForStepper(button);
      if (!input) return;

      const min = getMin(input);
      const max = getMax(input);
      const current = parseNum(input.value);
      const action = button.dataset.stepperAction;
      const isLengthIncrease = isLengthInput(input) && action === 'increase';

      let atLimit = false;
      if (current !== null) {
        if (action === 'decrease') atLimit = current <= min;
        if (action === 'increase') atLimit = current >= max;
      }

      // Keep length + clickable at max so we can show net-length-alert feedback.
      if (isLengthIncrease && atLimit) {
        button.disabled = false;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('net-length-stepper-btn--at-limit');
        return;
      }

      button.classList.remove('net-length-stepper-btn--at-limit');
      button.disabled = atLimit;
      button.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
    });
  }

  function getInputForStepper(button) {
    const row = button.closest('.net-length-input-row');
    if (row) {
      const inRow = row.querySelector(
        'input[id^="net-length-input-"], input[id^="net-height-input-"], input[type="number"], input[type="text"], input'
      );
      if (inRow) return inRow;
    }

    const container = button.closest('[id^="net-length-calculator-container-"]');
    if (!container) return null;

    const blockId = container.dataset.blockId;
    return (
      document.getElementById(`net-length-input-${blockId}`) ||
      document.getElementById(`net-height-input-${blockId}`)
    );
  }

  function applyStep(input, action) {
    const step = parseNum(input.step) || 0.1;
    const min = getMin(input);
    const max = getMax(input);
    let current = parseNum(input.value);

    if (current === null) current = min;

    if (action === 'increase') {
      const next = Math.round((current + step) * 10) / 10;
      if (next > max) {
        if (isLengthInput(input)) {
          showLengthFeedback(input, next);
        }
        return input;
      }
      current = next;
    } else {
      const next = Math.round((current - step) * 10) / 10;
      if (next < min) return input;
      current = next;
    }

    input.value = formatForInput(current, input);
    input.dataset.netzeCommittedValue = input.value;
    hideLengthAlert(input);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return input;
  }

  function scheduleCalculation(input) {
    const existing = calculatorTimers.get(input);
    if (existing) window.clearTimeout(existing);

    calculatorTimers.set(
      input,
      window.setTimeout(() => {
        calculatorTimers.delete(input);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        if (document.activeElement !== input) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, CALC_DELAY_MS)
    );
  }

  function onStepperPointerDown(event) {
    const button = event.target.closest(STEPPER_SELECTOR);
    if (!button) return;

    // Prevent text selection and double-tap zoom on mobile; keep mouse clicks normal.
    if (event.pointerType === 'touch' || event.pointerType === 'pen') {
      event.preventDefault();
    }
  }

  function onStepperTouchEnd(event) {
    const inStepper = event.target.closest(
      `${STEPPER_SELECTOR}, input[id^="net-length-input-"], input[id^="net-height-input-"]`
    );
    if (!inStepper) return;

    const container = event.currentTarget;
    const now = Date.now();
    const lastTap = Number(container.dataset.netzeLastTap || 0);

    if (now - lastTap <= 300) {
      event.preventDefault();
    }

    container.dataset.netzeLastTap = String(now);
  }

  function onStepperClick(event) {
    const button = event.target.closest(STEPPER_SELECTOR);
    if (!button) return;

    const action = button.dataset.stepperAction;
    if (action !== 'increase' && action !== 'decrease') return;

    const input = getInputForStepper(button);
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (document.activeElement === input) {
      input.blur();
    }

    const before = input.value;
    applyStep(input, action);

    if (input.value === before) {
      if (isLengthInput(input) && action === 'increase') {
        const max = getMax(input);
        const current = parseNum(input.value);
        const step = parseNum(input.step) || 0.1;
        if (current !== null && current >= max) {
          showLengthFeedback(input, Math.round((current + step) * 10) / 10);
        }
      }
      return;
    }

    input.dataset.netzeCommittedValue = input.value;

    const calculator = button.closest('[id^="net-length-calculator-container-"]');
    if (calculator) updateStepperButtons(calculator);

    scheduleCalculation(input);
  }

  function enforceMobileNoZoomInput(input) {
    if (!isMobileViewport()) return;

    const computedPx = Number.parseFloat(window.getComputedStyle(input).fontSize);
    if (!Number.isFinite(computedPx) || computedPx < 16) {
      input.style.setProperty('font-size', '16px', 'important');
    }
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function onInputFocus(input) {
    enforceMobileNoZoomInput(input);
    input.dataset.netzeCommittedValue = input.value;

    if (isDimensionInput(input)) {
      beginDimensionInputEdit(input);
      if (isLengthInput(input)) {
        hideLengthAlert(input);
      }
      window.requestAnimationFrame(() => {
        if (document.activeElement === input) {
          input.select();
        }
      });
    }
  }

  function commitDimensionInputOnBlur(input, container) {
    endDimensionInputEdit(input);

    const previous = input.dataset.netzeCommittedValue ?? input.value;
    const parsed = parseNum(input.value);
    const isEmpty = String(input.value ?? '').trim() === '';

    if (isEmpty || parsed === null) {
      input.value = previous;
      if (isLengthInput(input)) {
        hideLengthAlert(input);
      }
    } else {
      if (isLengthInput(input)) {
        const message = getLengthAttemptMessage(input, parsed);
        clampInputValue(input, { showTooltip: false });
        if (message) {
          showLengthFeedback(input, parsed);
        } else {
          hideLengthAlert(input);
        }
      } else {
        clampInputValue(input, { showTooltip: false });
      }
      input.dataset.netzeCommittedValue = input.value;
    }

    updateStepperButtons(container);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function commitLengthInputOnBlur(input, container) {
    commitDimensionInputOnBlur(input, container);
  }

  function onInputBlur(input, container) {
    if (isDimensionInput(input)) {
      commitDimensionInputOnBlur(input, container);
      return;
    }

    clampInputValue(input);
    input.dataset.netzeCommittedValue = input.value;
    updateStepperButtons(container);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function bindInputClamp(input, container) {
    if (input.dataset.netzeInputClampBound === 'true') return;
    input.dataset.netzeInputClampBound = 'true';

    enforceMobileNoZoomInput(input);
    input.dataset.netzeCommittedValue = input.value;

    if (isDimensionInput(input)) {
      input.addEventListener('input', blockDimensionInputEventsWhileEditing, true);
      input.addEventListener('change', blockDimensionInputEventsWhileEditing, true);
    }

    input.addEventListener('focus', () => onInputFocus(input));

    input.addEventListener('input', () => {
      if (isDimensionInputEditing(input)) return;

      updateStepperButtons(container);

      const parsed = parseNum(input.value);
      if (!isMobileViewport() || parsed !== null) {
        scheduleCalculation(input);
        return;
      }

      const existing = calculatorTimers.get(input);
      if (existing) window.clearTimeout(existing);
      calculatorTimers.delete(input);
    });

    input.addEventListener('blur', () => {
      if (isDimensionInput(input)) {
        input.removeEventListener('input', blockDimensionInputEventsWhileEditing, true);
        input.removeEventListener('change', blockDimensionInputEventsWhileEditing, true);
      }
      onInputBlur(input, container);
    });
  }

  function normalizeStepperRows(container) {
    container.querySelectorAll('.net-length-input-row').forEach((row) => {
      if (row.querySelector('.net-length-number-stepper')) return;

      const input = row.querySelector(
        'input[id^="net-length-input-"], input[id^="net-height-input-"], input.net-length-calculator-input'
      );
      if (!input) return;

      const stepperParts = Array.from(row.children).filter(
        (child) =>
          child === input ||
          (child.matches('.net-length-stepper-btn') && !child.closest('.net-length-number-stepper'))
      );

      if (stepperParts.length < 3) return;

      const stepper = document.createElement('div');
      stepper.className = 'net-length-number-stepper';
      row.insertBefore(stepper, stepperParts[0]);
      stepperParts.forEach((part) => stepper.appendChild(part));
    });
  }

  function bindCalculator(container) {
    normalizeStepperRows(container);
    if (container.dataset.netzeStepperTouchBound === 'true') return;
    container.dataset.netzeStepperTouchBound = 'true';

    container.addEventListener('pointerdown', onStepperPointerDown, { capture: true, passive: false });
    container.addEventListener('touchend', onStepperTouchEnd, { passive: false });
    container.addEventListener('click', onStepperClick, true);

    container
      .querySelectorAll('input[id^="net-length-input-"], input[id^="net-height-input-"]')
      .forEach((input) => {
        if (isLengthInput(input) || isHeightInput(input)) {
          input.min = '1.0';
        } else {
          const attrMin = parseNum(input.min);
          if (attrMin === null || attrMin < ABSOLUTE_MIN) input.min = String(ABSOLUTE_MIN);
        }
        bindInputClamp(input, container);
      });

    clampInputsInContainer(container, { showTooltip: false });
    updateStepperButtons(container);
  }

  function clampInputsInContainer(container, options = {}) {
    container
      .querySelectorAll('input[id^="net-length-input-"], input[id^="net-height-input-"]')
      .forEach((input) => {
        if (isDimensionInputEditing(input)) return;
        clampInputValue(input, options);
        input.dataset.netzeCommittedValue = input.value;
      });
  }

  function refreshCalculator(container) {
    clampInputsInContainer(container, { showTooltip: false });
    updateStepperButtons(container);
  }

  function initNetzeSteppers() {
    document.querySelectorAll('[id^="net-length-calculator-container-"]').forEach(bindCalculator);
  }

  window.NetzeStepperTouch = {
    refreshCalculator,
  };

  let stepperInitScheduled = false;

  function scheduleInitNetzeSteppers() {
    if (stepperInitScheduled) return;
    stepperInitScheduled = true;
    window.requestAnimationFrame(() => {
      stepperInitScheduled = false;
      initNetzeSteppers();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNetzeSteppers);
  } else {
    initNetzeSteppers();
  }

  const observer = new MutationObserver(() => scheduleInitNetzeSteppers());
  observer.observe(document.body, { childList: true, subtree: true });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(initNetzeSteppers));
  }
})();
